'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Icon from '../icon';
import { ICONS, type IconType } from '../icon/icon';

export type KeyFact = {
	icon: IconType;
	label: string;
};

// Up to this many tiles lay out in rows; past it they scroll on mobile and
// fill the description column on desktop (Figma "Fact icons", Hotel Intro Handoff).
export const WRAP_LIMIT = 6;

export type Props = {
	facts: KeyFact[];
	/** Blacklight does not truncate; the section decides. IAI-50 change order, 27 Jul 2026. */
	maxTiles?: number;
	className?: string;
};

export default function IconList({ facts, maxTiles, className = '' }: Props): ReactElement | null {
	const visible = facts
		.filter(
			fact =>
				fact.icon &&
				ICONS.includes(fact.icon) &&
				typeof fact.label === 'string' &&
				fact.label.trim()
		)
		.slice(0, maxTiles === undefined ? undefined : Math.max(0, maxTiles));

	if (visible.length < 3) return null;

	const overflows = visible.length > WRAP_LIMIT;
	// Desktop: 4-6 tiles spread edge to edge (Figma "Auto"); 3 tiles sit left at inline-2xl (Figma 10721:69346).
	const spreadsOnDesktop = visible.length >= 4;

	return (
		<ul
			className={cn(
				'items-start',
				overflows
					? // Mobile 7+ (Figma 10818:109515): one scrolling row of 90px tiles with
						// grid-gutter gaps and stack-s top/bottom padding. It runs through the
						// wide grid's gutter to the screen edge so tiles scrolled past the
						// content edge aren't cropped, while the first tile still rests on it.
						// Desktop (Figma 10818:109263) spreads tiles edge to edge across the
						// description column, wrapping only at its end (IAI-362, 1 Oct 2026).
						'-mx-(--wide-grid-gutter) flex gap-x-grid-gutter overflow-x-auto px-(--wide-grid-gutter) py-stack-s scroll-px-(--wide-grid-gutter) md:mx-0 md:grid md:grid-cols-[repeat(auto-fit,var(--spacing-900))] md:justify-between md:gap-y-200 md:overflow-visible md:p-0'
					: // Mobile 3-6 (Figma 7996:451031 / 7996:451211 / 7702:120009): a
						// grid-gutter grid of equal columns, three across (five leaves the last
						// row's two tiles on the left) or four across for exactly four.
						cn(
							'grid gap-grid-gutter md:flex md:flex-nowrap md:gap-0',
							visible.length === 4 ? 'grid-cols-4' : 'grid-cols-3',
							spreadsOnDesktop ? 'md:justify-between' : 'md:justify-start md:gap-x-inline-2xl'
						),
				className,
				overflows && '@min-[1400px]:max-w-none'
			)}
		>
			{visible.map((fact, index) => (
				<li
					key={`${fact.icon}-${index}`}
					className={cn(
						'flex min-w-0 flex-col items-center gap-y-stack-xs text-center',
						overflows ? 'w-900 shrink-0' : 'md:w-900 md:flex-none'
					)}
				>
					<span
						className="flex aspect-square w-full max-w-16 items-center justify-center rounded-full bg-warm-stone-20"
						aria-hidden="true"
					>
						<Icon
							icon={fact.icon}
							className="text-[32px] text-black md:text-[24px] lg:text-[32px]"
						/>
					</span>
					<span className="text-caption line-clamp-2 text-text-heading">
						<BevSafeText>{fact.label}</BevSafeText>
					</span>
				</li>
			))}
		</ul>
	);
}
